import React, { FC } from "react";
import { t, Trans } from "@helpers/translate";
import { GetStaticPaths, GetStaticProps } from "next";
import { graphql } from "@helpers/graphql";
import {
  CityBySlugQuery,
  ReviewsQuery,
  SupplierOrderBy,
  SuppliersQuery,
} from "@graphql/types";
import { getNodes } from "@helpers/getNodes";
import { searchURL } from "@helpers/searchURL";
import { CACHE_TIME, PLACEHOLDER_IMAGE } from "@constants";
import { isInValidSlug } from "@helpers/isInValidSlug";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import LoadingPage from "@components/pages/LoadingPage";
import Head from "@components/common/Head";
import ProductMicroFormat from "@components/common/MicroFormat/ProductMicroFormat";
import Hero from "@components/common/Hero/Hero";
import { Container } from "@components/ui/Container";
import { CategoriesLinks } from "@components/pages/SearchPage/CategoriesLinks";
import SuppliersBlock from "@components/common/SupplierBlock";
import { ButtonLink } from "@components/ui/Button";
import { CitiesBlock } from "@components/common/LocationBlock/CitiesBlock";
import Stats from "@components/common/Stats";
import SeoContentBlock from "@components/common/SeoContentBlock";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { isInValidCountrySlug } from "@helpers/isInValidCountrySlug";
import { Divider } from "@components/ui/Divider";
import {
  CityFooterData,
  getCityFooterData,
} from "@components/common/SEO/utils/getCityFooterData";
import { SEOCityLinks } from "@components/common/SEO/SEOCityLinks";
import Reviews from "@components/common/Reviews";
import ScrollToTop from "@components/ui/ScrollToTop/ScrollToTop";

const getSuppliersVariables = (citySlug: string) => {
  return {
    first: 8,
    filters: {
      CITIES_SLUGS: [citySlug],
      ORDER_BY: SupplierOrderBy.Recommended,
    },
  };
};

const getNewSuppliersVariables = (citySlug: string) => {
  return {
    first: 8,
    filters: {
      CITIES_SLUGS: [citySlug],
      ORDER_BY: SupplierOrderBy.CreatedAtDesc,
    },
  };
};

const getReviewsVariables = (CITY_SLUG?: string) => {
  return {
    first: 3,
    filters: {
      CITY_SLUG: CITY_SLUG,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.city as string;

  // This is to prevent un-necessary calls to database when slug is not proper value
  if (isInValidSlug(slug)) {
    return { notFound: true };
  }

  const countrySlug = (params?.country as string).toLocaleLowerCase();
  if (isInValidCountrySlug(countrySlug) === false) {
    return { notFound: true };
  }

  const city = await graphql.cityBySlug({
    slug,
  });

  if (!city.cityBySlug) {
    return { notFound: true };
  }

  const singaporeCityId = 4386;

  // nextjs dont work when click in bradcrumb
  if (singaporeCityId === city.cityBySlug?.id) {
    return {
      redirect: {
        destination: "/singapore/guides",
        statusCode: 301,
      },
    };
  }

  // For better seo, add redirect to proper slug url in case some unwanted params in the country.
  // http://localhost:3000/aaa/new-york - here we use only city and so aaa is ignored
  // TO make sure our seo is not effected , always redirect to our actual
  if (city.cityBySlug?.country?.slug != countrySlug) {
    return {
      redirect: {
        destination: city.cityBySlug?.url
          ? `${city.cityBySlug?.url}/guides`
          : "/",
        permanent: true,
      },
    };
  }

  const suppliers = await graphql.suppliers(getSuppliersVariables(slug));

  if (suppliers.suppliers.edges.length === 0) {
    return {
      redirect: {
        destination: "/",
        statusCode: 301,
      },
    };
  }

  const newSuppliers = await graphql.suppliers(getNewSuppliersVariables(slug));

  return {
    props: {
      initialData: {
        city,
        suppliers,
        newSuppliers,
        reviews: await graphql.reviews(getReviewsVariables(slug)),
        footer: await getCityFooterData(city.cityBySlug),
      },
    },
    revalidate: CACHE_TIME, //  1 hour cache
  };
};

type Props = {
  initialData: {
    city?: CityBySlugQuery;
    suppliers?: SuppliersQuery;
    newSuppliers?: SuppliersQuery;
    reviews?: ReviewsQuery;
    reviewsCount?: number;
    reviewsRate?: number;
    footer: CityFooterData;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  if (!initialData) {
    return <LoadingPage />;
  }

  const city = initialData.city?.cityBySlug;
  const cityName = city?.name;
  const countryName = city?.country.name;
  const isCountryActive = city?.country?.isActive;

  const noIndexFollow =
    !isCountryActive || countryName === "Togo" ? true : false;

  return (
    <>
      <Head
        title={t({
          id: "city.suppliers.seo.title",
          message: `${cityName} Private Tour Guides, Find a Local Guide in ${cityName}`,
          values: {
            cityName: `${cityName}`,
          },
        })}
        description={getMetaDataDescription(
          t({
            id: "city.suppliers.seo.description",
            message: `Contact a private tour guide to plan your ${cityName}, ${countryName} trip,  Save your time!, Request your ${cityName} Itinerary, and Book Online!`,
            values: {
              cityName: `${cityName}`,
              countryName: `${countryName}`,
            },
          })
        )}
        openGraph={{
          images: [{ url: city?.picture?.url || "" }],
        }}
        noindex={noIndexFollow}
        nofollow={noIndexFollow}
      />

      <ProductMicroFormat
        productID={`${city?.slug}_city_guides`}
        name={`${cityName} Private Tour Guides`}
        description={t({
          id: "city.suppliers.seo.description",
          values: { cityName, countryName },
        })}
        image={city?.picture?.serpImage || city?.picture?.url}
        reviewCount={initialData.city?.cityBySlug?.stats.reviewsCount}
      />
      <Hero
        placeholder={PLACEHOLDER_IMAGE}
        src={city?.picture?.url}
        alt="city image"
        title={t({
          id: "city.suppliers.hero.h1",
          message: `${cityName} Private Tour Guides`,
          values: {
            cityName: `${cityName}`,
          },
        })}
        subtitle={t({
          id: "city.suppliers.hero.h2",
          message: `Contact a private tour guide to plan your ${cityName} itinerary and Book Online.`,
          values: {
            cityName: `${cityName}`,
          },
        })}
        breadcrumb={[
          {
            text: city?.country.name || "",
            href: city?.country?.url || "",
          },
          {
            text: city?.name || "",
            href: city?.url || "",
          },
          {
            text: t({ id: "breadcrumb.guides", message: "Guides" }),
            href: `${city?.url}/guides`,
          },
        ]}
        reviewsCount={initialData.city?.cityBySlug?.stats.reviewsCount}
        reviewsRate={initialData.city?.cityBySlug?.stats.reviewsRating}
      />

      <div className="max-w-6xl mx-auto md:pt-4">
        {city && (
          <CategoriesLinks
            filters={{ city: city.slug }}
            className="sticky top-0 z-10 px-4 md:px-8 pb-4 pt-4 bg-white shadow-lg-simple md:shadow-none"
            hasGuides={city.hasSuppliers}
            hasTours={city.hasPrivateTours}
            hasOnlineTours={city.hasOnlineTours}
            hasTransportation={city.hasTransportationTours}
          />
        )}
        <Container className="pt-8 md:pt-0 pb-8 pr-0 md:pr-8">
          <Divider className="mt-4 mb-4 hidden md:flex" />
          {/* Popular city Tour Guides */}
          <SuppliersBlock
            title={t({
              id: "city.block.suppliers.title",
              message: `Popular ${cityName} Tour Guides`,
              values: {
                cityName: `${cityName}`,
              },
            })}
            data={getNodes(initialData.suppliers?.suppliers)}
            className="mb-10"
            enableSwipeOnMobile={true}
          >
            {initialData?.suppliers?.suppliers.pageInfo.hasNextPage && (
              <ButtonLink
                title="GoWithGuide - View All Tour Guides"
                href={getAbsoluteUrl(
                  searchURL({ t: "guides", city: city?.slug })
                )}
                variant="default-outline"
                className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
              >
                <Trans
                  id="city.block.suppliers.button"
                  values={{
                    cityName: `${cityName}`,
                  }}
                >
                  View All {cityName} Tour Guides
                </Trans>
              </ButtonLink>
            )}
          </SuppliersBlock>

          {/* New city Tour Guides */}
          <SuppliersBlock
            title={t({
              id: "city.block.new-guides.title",
              message: `New ${cityName} Tour Guides`,
              values: {
                cityName: `${cityName}`,
              },
            })}
            data={getNodes(initialData.newSuppliers?.suppliers)}
            className="mb-10"
            enableSwipeOnMobile={true}
          >
            {initialData?.newSuppliers?.suppliers.pageInfo.hasNextPage && (
              <ButtonLink
                title="GoWithGuide - View All Tour Guides"
                href={getAbsoluteUrl(
                  searchURL({ t: "guides", city: city?.slug })
                )}
                variant="default-outline"
                className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
              >
                <Trans
                  id="city.block.suppliers.button"
                  values={{
                    cityName: `${cityName}`,
                  }}
                >
                  View All {cityName} Tour Guides
                </Trans>
              </ButtonLink>
            )}
          </SuppliersBlock>

          {city && city.relatedCities && (
            <CitiesBlock
              title={t({
                id: "city.block.day-trips.title",
                message: `Day Trips from ${cityName}`,
                values: {
                  cityName: `${cityName}`,
                },
              })}
              data={city.relatedCities}
              className="my-10"
              enableSwipeOnMobile={true}
            />
          )}

          <div className="pr-4 md:pr-0">
            {/* Reviews */}
            <div id="reviews">
              <Reviews
                type="tours"
                initialData={initialData?.reviews}
                filters={getReviewsVariables(city?.slug)}
                title={t({
                  id: "city.block.review.title",
                  message: `${cityName} Tour Reviews`,
                  values: {
                    cityName: `${cityName}`,
                  },
                })}
              />
            </div>

            <div className="mb-10">
              <Stats />
            </div>

            <SeoContentBlock
              content={city?.seoContent.guides.content || ""}
              title={city?.seoContent.guides.title || ""}
            />

            <SEOCityLinks {...initialData.footer} />
          </div>

          <ScrollToTop />
        </Container>
      </div>
    </>
  );
};

export default Page;
